import {
    Box, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, ToggleButton, ToggleButtonGroup, Typography
} from "@mui/material";
import { useState } from "react";
import { useProjectFeed } from "../../misc/Feed";
import { PaletteGate } from "../../misc/PaletteGate";
import { findColor, projectColorText } from "../../misc/projectColors";
import { useWorkspace } from "../../misc/Workspace";
import { usePanels } from "../panel/usePanels";
import { ProjectCode } from "./ProjectCode";

type Filter = "active" | "archived" | "all";

export function ProjectListPage() {
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState<Filter>("active");
    const ws = useWorkspace();

    // the server pushes the list again after every change, so nothing here reloads
    const list = useProjectFeed({
        search: search,
        team: ws.team || undefined,
        archived: filter === "all" ? undefined : filter === "archived",
    });

    // the list carries whatever the search turned up; a link to something outside it
    // is fetched by the panel slot
    const panels = usePanels({ projects: list.projects });

    return (
        <PaletteGate>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Button variant="contained" size="small"
                        onClick={() => panels.openProject({})}>New project</Button>
            </div>

            <div style={{ height: 16 }} />
            <div style={{ display: "flex", gap: 8 }}>
                <TextField size="small" label="Search" fullWidth
                           value={search} onChange={e => setSearch(e.target.value)} />
                <ToggleButtonGroup size="small" exclusive value={filter}
                                   onChange={(_, value: Filter | null) => value && setFilter(value)}>
                    <ToggleButton value="active">Active</ToggleButton>
                    <ToggleButton value="archived">Archived</ToggleButton>
                    <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>
            </div>

            <div style={{ height: 16 }} />
            {list.LoadingOrErrorElement}
            {panels.LoadingOrErrorElement}

            <Paper>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ width: "100%" }}>Project</TableCell>
                            <TableCell align="right">Open tasks</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {list.projects.map(project => {
                            const color = findColor(project.color);

                            return (
                                <TableRow key={project.id} hover style={{ cursor: "pointer" }}
                                          selected={project.id === panels.project?.id}
                                          onClick={() => panels.openProject(project)}>
                                    <TableCell style={{ width: "100%" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Box sx={{ width: 24 }}>
                                                <ProjectCode code={project.code} color={project.color} />
                                            </Box>
                                            <Box component="span" sx={{
                                                px: 0.75, borderRadius: 1,
                                                bgcolor: color.bg, color: projectColorText(),
                                            }}>
                                                {project.name}
                                            </Box>
                                            {!ws.team && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {project.teamName}
                                                </Typography>
                                            )}
                                            {project.archived && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Archived
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">{project.openCount}</TableCell>
                                </TableRow>
                            );
                        })}
                        {!list.loading && list.projects.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                                    {search || filter !== "active" ? (
                                        <Typography variant="body2" color="text.secondary">
                                            No matching projects
                                        </Typography>
                                    ) : (
                                        <>
                                            <Typography variant="body2" color="text.secondary">No projects yet</Typography>
                                            <Typography variant="caption" color="text.secondary" component="div"
                                                        sx={{ mt: 0.5, maxWidth: 420, mx: "auto" }}>
                                                A project collects the tasks for one goal. Its code and colour
                                                show on each of its tasks, and it has a Gantt chart of them.
                                                Click New project to start.
                                            </Typography>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>

            {panels.element}
        </PaletteGate>
    );
}
